import type { AxiosInstance } from 'axios';
import type {
  HelpTooltipResponse,
  HelpArticleResponse,
  ListHelpArticlesQuery,
  ListHelpArticlesResponse,
  ListHelpReleaseNotesQuery,
  ListHelpReleaseNotesResponse,
  SubmitHelpArticleFeedbackRequest,
  SubmitHelpArticleFeedbackResponse,
  CachedHelpArticlesQuery,
  CachedHelpArticlesResponse,
  RecordHelpArticleProgressRequest,
  HelpArticleProgressResponse,
  ListHelpRecentProgressQuery,
  ListHelpRecentProgressResponse,
  WalkthroughResponse,
  WalkthroughWithProgressResponse,
  ListWalkthroughsResponse,
  ListPendingWalkthroughsResponse,
  ListWalkthroughsQuery,
  ListPendingWalkthroughsQuery,
  RecordWalkthroughProgressRequest,
  RecordWalkthroughProgressResponse,
  MarkReleaseNoteReadResponse,
  DismissReleaseNoteResponse,
} from '@commerce-os/shared-types';

/**
 * Phase 35 Session 35.1a — Help Center api-client wrappers.
 *
 * Surface that 35.2's `@commerce-os/help-client` SDK consumes. Every
 * wrapper resolves to a typed response from the backend resolver;
 * `null` when the caller isn't entitled (the resolver returns null +
 * the controller surfaces that as a 404 so callers can't enumerate
 * gated content).
 *
 * 35.1a behavior:
 *   - `getTooltip(client, helpKey)` returns the in-repo MDX fallback
 *     when one exists + the caller is entitled.
 *   - `getArticle` / `listArticles` / `listReleaseNotes` are stubs
 *     that return null / { data: [], cursor: null } / { data: [] }.
 *     They lock the wire format so 35.2's SDK + 35.3's dashboard
 *     can build against a stable contract.
 *
 * 35.1b lights up the CMS GROQ pipeline + Redis cache + webhook
 * invalidation; the wrapper signatures don't change.
 */

export function getTooltip(
  client: AxiosInstance,
  helpKey: string,
): Promise<HelpTooltipResponse | null> {
  return client
    .get<HelpTooltipResponse>(`/help/tooltips/${encodeURIComponent(helpKey)}`)
    .then((res) => res.data)
    .catch((err) => {
      if (err?.response?.status === 404) return null;
      throw err;
    });
}

export function getArticle(
  client: AxiosInstance,
  id: string,
): Promise<HelpArticleResponse | null> {
  return client
    .get<HelpArticleResponse>(`/help/articles/${encodeURIComponent(id)}`)
    .then((res) => res.data)
    .catch((err) => {
      if (err?.response?.status === 404) return null;
      throw err;
    });
}

export function listArticles(
  client: AxiosInstance,
  query: ListHelpArticlesQuery = {},
): Promise<ListHelpArticlesResponse> {
  return client
    .get<ListHelpArticlesResponse>('/help/articles', { params: query })
    .then((res) => res.data);
}

export function listReleaseNotes(
  client: AxiosInstance,
  query: ListHelpReleaseNotesQuery = {},
): Promise<ListHelpReleaseNotesResponse> {
  return client
    .get<ListHelpReleaseNotesResponse>('/help/release-notes', { params: query })
    .then((res) => res.data);
}

/**
 * Phase 35.3b — submit thumbs-up / thumbs-down feedback for an article.
 * Returns null when the caller isn't entitled to read the article
 * (resolver returns null → controller surfaces opaque 404). Defends
 * against feedback-probe enumeration of article ids.
 */
export function submitArticleFeedback(
  client: AxiosInstance,
  articleId: string,
  body: SubmitHelpArticleFeedbackRequest,
): Promise<SubmitHelpArticleFeedbackResponse | null> {
  return client
    .post<SubmitHelpArticleFeedbackResponse>(
      `/help/articles/${encodeURIComponent(articleId)}/feedback`,
      body,
    )
    .then((res) => res.data)
    .catch((err) => {
      if (err?.response?.status === 404) return null;
      throw err;
    });
}

/**
 * Phase 35.4a — fetch the per-org cached article catalog for the POS
 * offline help drawer. The 35.4b RN POS + 35.4c web POS consumers
 * persist the response into SQLite / IndexedDB respectively, then
 * re-read locally when the network's down.
 *
 * Uses the standard `If-None-Match` HTTP header round-trip:
 *   - When `previousEtag` is supplied + matches the server's current
 *     `catalogEtag`, the server returns 304 and this wrapper resolves
 *     to `null` so the caller knows to keep its existing local cache.
 *   - Otherwise resolves to the full response. Caller persists the
 *     new `catalogEtag` for the next sync.
 *
 * Empty `data: []` is a valid response (no articles target this
 * platform / locale combo for this org); treat it as "the server
 * confirms the cache should be empty" and clear local state.
 */
export function getCachedHelpArticles(
  client: AxiosInstance,
  query: CachedHelpArticlesQuery,
  previousEtag?: string | null,
): Promise<CachedHelpArticlesResponse | null> {
  const headers: Record<string, string> = {};
  if (previousEtag) {
    headers['If-None-Match'] = previousEtag;
  }
  return client
    .get<CachedHelpArticlesResponse>('/pos/help/cached-articles', {
      params: query,
      headers,
      // 304 should resolve, not throw.
      validateStatus: (status) => (status >= 200 && status < 300) || status === 304,
    })
    .then((res) => {
      if (res.status === 304) return null;
      return res.data;
    });
}

/**
 * Phase 35.6a — record cross-device read progress for an article.
 * Caller-scoped (writes to JWT subject's progress row). Server is
 * monotonic on `scrollDepth` + stamps `completedAt` exactly once when
 * scroll first crosses 90%.
 *
 * Throttle calls client-side (~1 write/sec/article) — the endpoint
 * itself isn't rate-limited but every write is an upsert + emit so
 * thrashing it gives no benefit.
 */
export function recordHelpArticleProgress(
  client: AxiosInstance,
  articleId: string,
  body: RecordHelpArticleProgressRequest,
): Promise<HelpArticleProgressResponse> {
  return client
    .post<HelpArticleProgressResponse>(
      `/help/progress/${encodeURIComponent(articleId)}`,
      body,
    )
    .then((res) => res.data);
}

/**
 * Phase 35.6a — top-N most recently active progress rows for the
 * caller. Powers the "Continue reading" block on the dashboard +
 * POS surfaces (35.6c) + Management app (35.6b).
 *
 * Returns the raw progress rows; consumer surfaces join article
 * metadata (title / summary) via the resolver if rendering a card.
 */
export function listHelpRecentProgress(
  client: AxiosInstance,
  query: ListHelpRecentProgressQuery = {},
): Promise<ListHelpRecentProgressResponse> {
  return client
    .get<ListHelpRecentProgressResponse>('/help/progress/recent', {
      params: query,
    })
    .then((res) => res.data);
}

// ─────────────────────────────────────────────────────────────────────────
// Phase 35.8a — Walkthrough + release-note action wrappers.
// ─────────────────────────────────────────────────────────────────────────

/**
 * `GET /help/walkthroughs/:id` — fetch a single walkthrough definition
 * + the caller's progress against it. Returns null on 404 (cross-org
 * probe / unknown id / caller not entitled — opaque-404 contract).
 */
export function getWalkthrough(
  client: AxiosInstance,
  id: string,
): Promise<WalkthroughWithProgressResponse | null> {
  return client
    .get<WalkthroughWithProgressResponse>(
      `/help/walkthroughs/${encodeURIComponent(id)}`,
      {
        validateStatus: (status) =>
          status >= 200 && status < 300 || status === 404,
      },
    )
    .then((res) => (res.status === 404 ? null : res.data));
}

/**
 * `GET /help/walkthroughs?platform=...&role=...&includeCompleted=true` —
 * full surface for the help drawer's "Walkthroughs" tab.
 */
export function listWalkthroughs(
  client: AxiosInstance,
  query: ListWalkthroughsQuery,
): Promise<ListWalkthroughsResponse> {
  return client
    .get<ListWalkthroughsResponse>('/help/walkthroughs', { params: query })
    .then((res) => res.data);
}

/**
 * `GET /help/walkthroughs/pending?platform=...&role=...` — auto-start
 * decision support. Runner picks the first entry as the auto-start
 * candidate; surfaces every walkthrough where the caller is eligible AND
 * either has no prior progress row OR the dismissed row's stored version
 * is older than the CMS's current version.
 */
export function listPendingWalkthroughs(
  client: AxiosInstance,
  query: ListPendingWalkthroughsQuery,
): Promise<ListPendingWalkthroughsResponse> {
  return client
    .get<ListPendingWalkthroughsResponse>('/help/walkthroughs/pending', {
      params: query,
    })
    .then((res) => res.data);
}

/**
 * `POST /help/walkthroughs/:id/progress` — write the caller's latest
 * progress state. Server enforces the legal-transition matrix (see the
 * shared-type `RecordWalkthroughProgressRequest` doc-comment) + emits
 * the right event (`HELP_WALKTHROUGH_STARTED` / `_STEP_ADVANCED` /
 * `_COMPLETED` / `_DISMISSED`) per state change.
 */
export function recordWalkthroughProgress(
  client: AxiosInstance,
  id: string,
  body: RecordWalkthroughProgressRequest,
): Promise<RecordWalkthroughProgressResponse> {
  return client
    .post<RecordWalkthroughProgressResponse>(
      `/help/walkthroughs/${encodeURIComponent(id)}/progress`,
      body,
    )
    .then((res) => res.data);
}

/**
 * `POST /help/release-notes/:id/mark-read` — mark a release note as
 * read for the caller. Idempotent — re-marking returns the same
 * `readAt` timestamp.
 */
export function markReleaseNoteRead(
  client: AxiosInstance,
  id: string,
): Promise<MarkReleaseNoteReadResponse> {
  return client
    .post<MarkReleaseNoteReadResponse>(
      `/help/release-notes/${encodeURIComponent(id)}/mark-read`,
    )
    .then((res) => res.data);
}

/**
 * `POST /help/release-notes/:id/dismiss` — dismiss a release note from
 * the caller's TopBar popover feed. Hides from the unread list but
 * remains visible in the back-catalog (Phase 35.8c).
 */
export function dismissReleaseNote(
  client: AxiosInstance,
  id: string,
): Promise<DismissReleaseNoteResponse> {
  return client
    .post<DismissReleaseNoteResponse>(
      `/help/release-notes/${encodeURIComponent(id)}/dismiss`,
    )
    .then((res) => res.data);
}
